'use client'
import { Text, View } from 'react-native'
import React from 'react'
import { createFormControl } from '@gluestack-ui/core/form-control/creator'
import { tva } from '@gluestack-ui/utils/nativewind-utils'
import { withStyleContext, useStyleContext } from '@gluestack-ui/utils/nativewind-utils'
import { cssInterop } from 'nativewind'
import type { VariantProps } from '@gluestack-ui/utils/nativewind-utils'
import { PrimitiveIcon } from '@gluestack-ui/core/icon/creator'

import { ErrorCircleGlyph } from '../../components/primitive-glyphs/glyphs'
import { FIELD_TEXT_CLASS } from '../../fieldTypography'

import { UIIcon } from '../icon/UIIcon'

const SCOPE = 'FORM_CONTROL'

const AOS_TEXT_SIZES = {
    sm: 'text-[0.8125rem] leading-[1.25rem]',
    md: FIELD_TEXT_CLASS,
    lg: 'text-[1rem] leading-[150%]',
} as const

const formControlStyle = tva({
    base: 'flex flex-col',
    variants: {
        size: {
            sm: '',
            md: '',
            lg: '',
        },
        // Figma disabled = the whole field group (label + input + additional
        // text) at 40% opacity. Modelled as a variant so it composes with the
        // caller's `class` without manual string concatenation.
        isDisabled: {
            true: 'opacity-40',
        },
    },
})

const formControlErrorIconStyle = tva({
    base: 'text-status-critical fill-none',
    variants: {
        size: {
            '2xs': 'h-3 w-3',
            'xs': 'h-3.5 w-3.5',
            'sm': 'h-4 w-4',
            'md': 'h-[18px] w-[18px]',
            'lg': 'h-5 w-5',
            'xl': 'h-6 w-6',
        },
    },
})

const formControlErrorStyle = tva({
    base: 'flex flex-row justify-start items-start mt-1 gap-1',
})

const formControlErrorTextStyle = tva({
    base: `font-semiBold ${FIELD_TEXT_CLASS} text-status-critical`,
    variants: {
        isTruncated: {
            true: 'web:truncate',
        },
        bold: {
            true: 'font-bold',
        },
        underline: {
            true: 'underline',
        },
        strikeThrough: {
            true: 'line-through',
        },
        size: {
            '2xs': 'text-2xs',
            'xs': 'text-xs',
            ...AOS_TEXT_SIZES,
            'xl': 'text-xl',
            '2xl': 'text-2xl',
            '3xl': 'text-3xl',
            '4xl': 'text-4xl',
            '5xl': 'text-5xl',
            '6xl': 'text-6xl',
        },
        sub: {
            true: 'text-xs',
        },
        italic: {
            true: 'italic',
        },
        highlight: {
            true: 'bg-yellow-500',
        },
    },
})

const formControlHelperStyle = tva({
    base: 'flex flex-row justify-start items-center mt-1',
})

const formControlHelperTextStyle = tva({
    base: 'text-typography-500',
    variants: {
        isTruncated: {
            true: 'web:truncate',
        },
        bold: {
            true: 'font-bold',
        },
        underline: {
            true: 'underline',
        },
        strikeThrough: {
            true: 'line-through',
        },
        size: {
            '2xs': 'text-2xs',
            'xs': 'text-xs',
            'sm': 'text-xs',
            'md': 'text-sm',
            'lg': 'text-base',
            'xl': 'text-xl',
            '2xl': 'text-2xl',
            '3xl': 'text-3xl',
            '4xl': 'text-4xl',
            '5xl': 'text-5xl',
            '6xl': 'text-6xl',
        },
        sub: {
            true: 'text-xs',
        },
        italic: {
            true: 'italic',
        },
        highlight: {
            true: 'bg-yellow-500',
        },
    },
})

const formControlLabelStyle = tva({
    base: 'flex flex-row justify-start items-center mb-1',
})

const formControlLabelTextStyle = tva({
    // AOS redesign (AOS-600): label = content/default. `typography-900` resolved
    // to near-black in the host SB Tailwind (it's not a themed CSS-var token),
    // so labels were unreadable in dark mode — use the baked light token.
    //
    // Sizes are the `Text` tokens verbatim (see components/text/variants.ts) —
    // read them from there, never re-type the px. `base` covers a label used
    // without a FormControl parent; the sm/md/lg entries below cover the parent
    // size context, which is what actually resolves (FormControl defaults to
    // `md`) and previously landed on Tailwind's `text-base` — 16px, one step off
    // spec, which is why call sites had to hand-write the px themselves.
    base: `font-medium ${FIELD_TEXT_CLASS} text-aos-text-default`,
    variants: {
        isTruncated: {
            true: 'web:truncate',
        },
        bold: {
            true: 'font-bold',
        },
        underline: {
            true: 'underline',
        },
        strikeThrough: {
            true: 'line-through',
        },
        size: {
            '2xs': 'text-2xs',
            'xs': 'text-xs',
            ...AOS_TEXT_SIZES,
            'xl': 'text-xl',
            '2xl': 'text-2xl',
            '3xl': 'text-3xl',
            '4xl': 'text-4xl',
            '5xl': 'text-5xl',
            '6xl': 'text-6xl',
        },
        sub: {
            true: 'text-xs',
        },
        italic: {
            true: 'italic',
        },
        highlight: {
            true: 'bg-yellow-500',
        },
    },
})

const formControlLabelAstrickStyle = tva({
    // The required asterisk sits inline with the label, so it tracks the label's
    // sizes exactly — keep this map in step with formControlLabelTextStyle.
    base: `font-medium ${FIELD_TEXT_CLASS} text-aos-text-default`,
    variants: {
        isTruncated: {
            true: 'web:truncate',
        },
        bold: {
            true: 'font-bold',
        },
        underline: {
            true: 'underline',
        },
        strikeThrough: {
            true: 'line-through',
        },
        size: {
            '2xs': 'text-2xs',
            'xs': 'text-xs',
            ...AOS_TEXT_SIZES,
            'xl': 'text-xl',
            '2xl': 'text-2xl',
            '3xl': 'text-3xl',
            '4xl': 'text-4xl',
            '5xl': 'text-5xl',
            '6xl': 'text-6xl',
        },
        sub: {
            true: 'text-xs',
        },
        italic: {
            true: 'italic',
        },
        highlight: {
            true: 'bg-yellow-500',
        },
    },
})

type IFormControlLabelAstrickProps = React.ComponentPropsWithoutRef<typeof Text> &
    VariantProps<typeof formControlLabelAstrickStyle>

const FormControlLabelAstrick = React.forwardRef<
    React.ComponentRef<typeof Text>,
    IFormControlLabelAstrickProps
>(function FormControlLabelAstrick({ className, ...props }, ref) {
    const { size: parentSize } = useStyleContext(SCOPE)

    return (
        <Text
            ref={ref}
            className={formControlLabelAstrickStyle({
                parentVariants: { size: parentSize },
                class: className,
            })}
            {...props}
        />
    )
})

export const UIFormControl = createFormControl({
    Root: withStyleContext(View, SCOPE),
    Error: View,
    ErrorText: Text,
    ErrorIcon: UIIcon,
    Label: View,
    LabelText: Text,
    LabelAstrick: FormControlLabelAstrick,
    Helper: View,
    HelperText: Text,
})

cssInterop(PrimitiveIcon, {
    className: {
        target: 'style',
        nativeStyleToProp: {
            height: true,
            width: true,
            fill: true,
            color: true,
            stroke: true,
        },
    },
})

type IFormControlProps = React.ComponentProps<typeof UIFormControl> &
    VariantProps<typeof formControlStyle>

const FormControl = React.forwardRef<React.ComponentRef<typeof UIFormControl>, IFormControlProps>(
    function FormControl({ className, size = 'md', ...props }, ref) {
        // Figma disabled = the whole field group (label + input + additional text)
        // at 40% opacity, driven here off the `isDisabled` prop as a single group
        // layer (no per-child compounding) and reliable on web where gluestack does
        // not propagate a `data-disabled` attribute to the label.
        return (
            <UIFormControl
                ref={ref}
                className={formControlStyle({
                    size,
                    isDisabled: props.isDisabled,
                    class: className,
                })}
                {...props}
                context={{ size }}
            />
        )
    },
)

type IFormControlErrorProps = React.ComponentProps<typeof UIFormControl.Error> &
    VariantProps<typeof formControlErrorStyle>

const FormControlError = React.forwardRef<
    React.ComponentRef<typeof UIFormControl.Error>,
    IFormControlErrorProps
>(function FormControlError({ className, children, ...props }, ref) {
    return (
        <UIFormControl.Error
            ref={ref}
            className={formControlErrorStyle({ class: className })}
            {...props}
        >
            {/* AOS redesign (AOS-600): Figma error row leads with a critical
                circle-X. Rendered cross-platform (explicit colours) so it shows
                on web and mobile, unlike the gluestack currentColor icon. */}
            <View className='shrink-0'>
                <ErrorCircleGlyph size={20} />
            </View>
            {children}
        </UIFormControl.Error>
    )
})

type IFormControlErrorTextProps = React.ComponentProps<typeof UIFormControl.Error.Text> &
    VariantProps<typeof formControlErrorTextStyle>

const FormControlErrorText = React.forwardRef<
    React.ComponentRef<typeof UIFormControl.Error.Text>,
    IFormControlErrorTextProps
>(function FormControlErrorText({ className, size, ...props }, ref) {
    const { size: parentSize } = useStyleContext(SCOPE)
    return (
        <UIFormControl.Error.Text
            className={formControlErrorTextStyle({
                parentVariants: { size: parentSize },
                size,
                class: className,
            })}
            ref={ref}
            {...props}
        />
    )
})

type IFormControlErrorIconProps = React.ComponentProps<typeof UIFormControl.Error.Icon> &
    VariantProps<typeof formControlErrorIconStyle> & {
        height?: number
        width?: number
    }

const FormControlErrorIcon = React.forwardRef<
    React.ComponentRef<typeof UIFormControl.Error.Icon>,
    IFormControlErrorIconProps
>(function FormControlErrorIcon({ className, size, ...props }, ref) {
    const { size: parentSize } = useStyleContext(SCOPE)

    if (typeof size === 'number') {
        return (
            <UIFormControl.Error.Icon
                ref={ref}
                {...props}
                className={formControlErrorIconStyle({ class: className })}
                size={size}
            />
        )
    } else if ((props.height !== undefined || props.width !== undefined) && size === undefined) {
        return (
            <UIFormControl.Error.Icon
                ref={ref}
                {...props}
                className={formControlErrorIconStyle({ class: className })}
            />
        )
    }
    return (
        <UIFormControl.Error.Icon
            className={formControlErrorIconStyle({
                parentVariants: { size: parentSize },
                size,
                class: className,
            })}
            {...props}
        />
    )
})

type IFormControlLabelProps = React.ComponentProps<typeof UIFormControl.Label> &
    VariantProps<typeof formControlLabelStyle> & {
        htmlFor?: string
        role?: string
    }

const FormControlLabel = React.forwardRef<
    React.ComponentRef<typeof UIFormControl.Label>,
    IFormControlLabelProps
>(function FormControlLabel({ className, ...props }, ref) {
    return (
        <UIFormControl.Label
            ref={ref}
            className={formControlLabelStyle({ class: className })}
            {...props}
        />
    )
})

type IFormControlLabelTextProps = React.ComponentProps<typeof UIFormControl.Label.Text> &
    VariantProps<typeof formControlLabelTextStyle>

const FormControlLabelText = React.forwardRef<
    React.ComponentRef<typeof UIFormControl.Label.Text>,
    IFormControlLabelTextProps
>(function FormControlLabelText({ className, size, ...props }, ref) {
    const { size: parentSize } = useStyleContext(SCOPE)

    return (
        <UIFormControl.Label.Text
            className={formControlLabelTextStyle({
                parentVariants: { size: parentSize },
                size,
                class: className,
            })}
            ref={ref}
            {...props}
        />
    )
})

type IFormControlHelperProps = React.ComponentProps<typeof UIFormControl.Helper> &
    VariantProps<typeof formControlHelperStyle>

const FormControlHelper = React.forwardRef<
    React.ComponentRef<typeof UIFormControl.Helper>,
    IFormControlHelperProps
>(function FormControlHelper({ className, ...props }, ref) {
    return (
        <UIFormControl.Helper
            ref={ref}
            className={formControlHelperStyle({
                class: className,
            })}
            {...props}
        />
    )
})

type IFormControlHelperTextProps = React.ComponentProps<typeof UIFormControl.Helper.Text> &
    VariantProps<typeof formControlHelperTextStyle>

const FormControlHelperText = React.forwardRef<
    React.ComponentRef<typeof UIFormControl.Helper.Text>,
    IFormControlHelperTextProps
>(function FormControlHelperText({ className, size, ...props }, ref) {
    const { size: parentSize } = useStyleContext(SCOPE)

    return (
        <UIFormControl.Helper.Text
            className={formControlHelperTextStyle({
                parentVariants: { size: parentSize },
                size,
                class: className,
            })}
            ref={ref}
            {...props}
        />
    )
})

FormControl.displayName = 'FormControl'
FormControlError.displayName = 'FormControlError'
FormControlErrorText.displayName = 'FormControlErrorText'
FormControlErrorIcon.displayName = 'FormControlErrorIcon'
FormControlLabel.displayName = 'FormControlLabel'
FormControlLabelText.displayName = 'FormControlLabelText'
FormControlLabelAstrick.displayName = 'FormControlLabelAstrick'
FormControlHelper.displayName = 'FormControlHelper'
FormControlHelperText.displayName = 'FormControlHelperText'

export {
    FormControl,
    FormControlError,
    FormControlErrorText,
    FormControlErrorIcon,
    FormControlLabel,
    FormControlLabelText,
    FormControlLabelAstrick,
    FormControlHelper,
    FormControlHelperText,
}
